import { useEffect, useState } from "react";
import axios from "axios";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Users, FileText, ListChecks, MapPin, UserCog, Package, TrendingUp, AlertTriangle, Truck, DollarSign, ShoppingBag, Store, Crown, Settings, CreditCard } from "lucide-react";
import ClientesPage from "./ClientesPage";
import NovaPromissoriaPage from "./NovaPromissoriaPage";
import DividasPage from "./DividasPage";
import HomePage from "./HomePage";
import ControleRotasPage from "./ControleRotasPage";
import VendedoresPage from "./VendedoresPage";
import ProdutosPage from "./ProdutosPage";
import EstoqueCarroPage from "./EstoqueCarroPage";
import MovimentacoesPage from "./MovimentacoesPage";
import FluxoCaixaPage from "./GestaoFinanceiraPage";
import CaixaCarroPage from "./CaixaCarroPage";
import PedidosLojaPage from "./PedidosLojaPage";
import LojaOnlinePage from "./LojaOnlinePage";
import GestaoCotacaoAssinantesPage from "./GestaoCotacaoAssinantesPage";
import GestaoAssinantesUnificadaPage from "./GestaoAssinantesUnificadaPage";
import ConfiguracoesLojaPage from "./ConfiguracoesLojaPage";
import PlanosPage from "./PlanosPage";
import MinhaAssinaturaPage from "./MinhaAssinaturaPage";

const API = `${window.location.origin}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.tipo === 'admin' || !user.tipo;
  const isSecretario = user.tipo === 'secretario';
  const isVendedor = user.tipo === 'vendedor';
  const isSuperAdmin = !!user.super_admin;
  const permissoes = user.permissoes || {};
  const [pedidosPendentes, setPedidosPendentes] = useState(0);
  const [dividasSemFoto, setDividasSemFoto] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/loja/pedidos-pendentes`);
        setPedidosPendentes(Array.isArray(data) ? data.length : 0);
      } catch {}
      try {
        const { data } = await axios.get(`${API}/promissorias/sem-foto/count`);
        setDividasSemFoto(data?.count || 0);
      } catch {}
    };
    load();
    // Carrega logo da loja (uma vez ao montar)
    (async () => {
      try {
        const { data } = await axios.get(`${API}/loja/config`);
        const path = data?.perfil_loja?.logo_path;
        if (path) {
          const fn = path.split('/').pop();
          setLogoUrl(`${API}/loja/logo-file/${fn}`);
        }
      } catch {}
    })();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const menuItems = [
    { path: '/', label: 'Início', icon: ListChecks },
    { path: '/clientes', label: 'Clientes', icon: Users },
    { path: '/nova-promissoria', label: 'Nova Promissória', icon: FileText },
    ...((isAdmin || isSecretario && permissoes.acessar_dividas || isVendedor) ? [{ path: '/dividas', label: 'Dívidas', icon: ListChecks, badge: dividasSemFoto }] : []),
    { path: '/controle-rotas', label: 'Controle de Rotas', icon: MapPin },
    { path: '/pedidos-loja', label: 'Pedidos Loja', icon: ShoppingBag, badge: pedidosPendentes },
    ...((isAdmin || isSecretario && permissoes.acessar_fluxo_caixa) ? [{ path: '/fluxo-caixa', label: 'Gestão Financeira', icon: DollarSign }] : []),
    ...(isAdmin ? [{ path: '/acessos', label: 'Acessos', icon: UserCog }] : []),
    ...((isAdmin || isSecretario) ? [{ path: '/produtos', label: 'Catálogo de Produtos', icon: Package }] : []),
    { path: '/estoque-carro', label: 'Estoque do Carro', icon: Truck },
    ...((isAdmin || isSecretario) ? [{ path: '/movimentacoes', label: 'Movimentações', icon: TrendingUp }] : []),
    ...(isAdmin ? [{ path: '/configuracoes-loja', label: 'Configurações da Loja', icon: Settings }] : []),
    ...(isAdmin ? [{ path: '/assinatura', label: 'Minha Assinatura', icon: CreditCard }] : []),
    ...(isAdmin ? [{ path: '/planos', label: 'Planos', icon: Crown }] : []),
    ...(isSuperAdmin ? [{ path: '/gestao-assinantes', label: 'Gestão de Assinantes', icon: Crown }] : []),
    ...(isSuperAdmin ? [{ path: '/cotacoes/login', label: 'Sistema de Cotações', icon: FileText, isExternal: true }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain bg-white" />
                ) : (
                  <img src="/logo-compromisso.png" alt="Compromisso Digital" className="w-full h-full object-cover" />
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Work Sans'}}>Compromisso Digital</h1>
                <p className="text-xs text-gray-500">{user.nome_loja || user.nome}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-button"
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation - hidden on home page */}
      {location.pathname !== '/' && (
        <nav className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1 overflow-x-auto">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${
                      isActive 
                        ? 'border-blue-500 text-blue-600 font-medium' 
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {item.badge > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse">{item.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/clientes" element={<ClientesPage />} />
          <Route path="/nova-promissoria" element={<NovaPromissoriaPage />} />
          <Route path="/dividas" element={<DividasPage />} />
          <Route path="/controle-rotas" element={<ControleRotasPage />} />
          <Route path="/pedidos-loja" element={<PedidosLojaPage />} />
          <Route path="/loja-online" element={<LojaOnlinePage />} />
          {(isAdmin || (isSecretario && permissoes.acessar_fluxo_caixa)) && <Route path="/fluxo-caixa" element={<FluxoCaixaPage />} />}
          {isAdmin && <Route path="/acessos" element={<VendedoresPage />} />}
          {(isAdmin || isSecretario) && <Route path="/produtos" element={<ProdutosPage />} />}
          {/* /produtos/ranking mantido como redirect para /produtos (aba Ranking) — bookmarks antigos */}
          <Route path="/produtos/ranking" element={<Navigate to="/produtos" replace />} />
          {/* /estoque-loja foi migrado para /produtos — mantemos redirect para bookmarks antigos */}
          <Route path="/estoque-loja" element={<Navigate to="/produtos" replace />} />
          <Route path="/estoque-carro" element={<EstoqueCarroPage />} />
          <Route path="/caixa-carro" element={<CaixaCarroPage />} />
          {(isAdmin || isSecretario) && <Route path="/movimentacoes" element={<MovimentacoesPage />} />}
          {/* /alertas-estoque foi migrado para a aba "Alertas" em /produtos */}
          <Route path="/alertas-estoque" element={<Navigate to="/produtos" replace />} />
          {isAdmin && <Route path="/configuracoes-loja" element={<ConfiguracoesLojaPage />} />}
          {isAdmin && <Route path="/planos" element={<PlanosPage />} />}
          {isAdmin && <Route path="/assinatura" element={<MinhaAssinaturaPage />} />}
          <Route path="/gestao-assinantes" element={<GestaoAssinantesUnificadaPage />} />
          <Route path="/gestao-cotacao" element={<GestaoCotacaoAssinantesPage />} />
        </Routes>
      </main>
    </div>
  );
}
